// DailyDevotionalCard — a PERMANENT Home-page card that surfaces a
// fresh ~150-word AI devotional every day on a rotating Bible verse.
// Backed by GET /api/today/daily-devotional.
//
// Why this exists separate from `TodayBanner.jsx`:
//   - The holiday banner only renders on recognized holidays AND can
//     be dismissed for the day with an X button. The user hit that
//     X once and lost their devotional for the whole day.
//   - This card NEVER 404s and has NO dismiss control — it's the
//     promise of "one verse + one reflection every morning, right
//     where I open the app".
//
// Behavior:
//   - On mount: fetches the day's verse + AI reflection (cached
//     server-side per calendar day so it's near-instant after the
//     first user hits it).
//   - "Read aloud" button: pipes the devotional text through
//     /api/tts/speak (OpenAI TTS, studio-quality "coral" voice) and
//     plays the MP3 inline. Tap again to pause.
import React, { useEffect, useState } from "react";
import { BookOpenText, Sparkle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import ReadAloudButton from "./ReadAloudButton";
import Tr from "./Tr";
import { useAudio } from "../context/AudioContext";
import useCaptionTranslation, { useCaptionTranslationWithPending } from "../hooks/useCaptionTranslation";

export default function DailyDevotionalCard() {
  const [data, setData] = useState(null);   // { text, verse: {text, reference}, date }
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { language } = useAudio();
  // Dynamic content (devotional body + verse quote) comes back in
  // English from the backend. When the user is on a non-English UI,
  // pipe both through /api/translate (cached per-language) so the
  // whole card reads in their language. Translation is async — until
  // it arrives we keep showing the English so the card never blanks.
  // `isPending` flips true while the translation is in flight so the
  // card can render a "Translating…" pulse instead of looking frozen.
  const { translated: translatedBody,  isPending: pendingBody  } = useCaptionTranslationWithPending(data?.text || "",          language);
  const { translated: translatedVerse, isPending: pendingVerse } = useCaptionTranslationWithPending(data?.verse?.text || "", language);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = {};
    if (language && language !== "en") params.lang = language;
    api.get("/today/daily-devotional", { params, timeout: 30000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // Re-fetch when language changes so we get the server-translated
    // payload directly (instant on a warm cache) instead of relying
    // on the client-side Claude round-trip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  if (loading) {
    return (
      <section
        data-testid="daily-devotional-card-loading"
        className="rounded-3xl bg-gradient-to-br from-forest/95 via-forest to-terracotta/55 text-cream p-7 md:p-10 shadow-xl relative overflow-hidden"
      >
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-cream/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="w-40 h-5 rounded-full bg-cream/20 animate-pulse" />
          <div className="mt-4 h-9 w-3/4 rounded-md bg-cream/20 animate-pulse" />
          <div className="mt-2 h-9 w-1/2 rounded-md bg-cream/20 animate-pulse" />
          <div className="mt-6 h-16 rounded-2xl bg-cream/10 animate-pulse" />
          <div className="mt-4 h-28 rounded-2xl bg-cream/10 animate-pulse" />
        </div>
      </section>
    );
  }

  if (!data) {
    // Soft failure: backend offline / LLM error. Skip the rail entirely
    // rather than parading an error banner across the Home page.
    return null;
  }

  return (
    <section
      data-testid="daily-devotional-card"
      className="rounded-3xl bg-gradient-to-br from-forest-400 via-forest to-terracotta text-cream shadow-2xl ring-1 ring-cream/15 relative overflow-hidden"
    >
      <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />
      <div className="absolute -left-10 -bottom-12 w-48 h-48 rounded-full bg-terracotta/30 blur-3xl pointer-events-none" />
      <div className="relative p-7 md:p-10 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] gap-7 lg:gap-12 items-start">
        {/* LEFT — eyebrow, heading, verse + read-aloud affordance */}
        <div>
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-200 inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> {t("today.devotionalLabel")}
          </span>
          <h3 className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] mt-2 leading-[1.08]">
            {t("today.devotionalHeading")}
          </h3>

          {data.verse && (
            <blockquote
              data-testid="daily-devotional-verse"
              className={`mt-6 px-4 py-3.5 rounded-2xl border bg-black/25 border-cream/20 flex gap-3 items-start transition-opacity ${pendingVerse ? "opacity-70" : ""}`}
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
            >
              <BookOpenText size={20} weight="duotone" className="shrink-0 mt-0.5 text-amber-200" />
              <div>
                <p className="italic text-sm md:text-base leading-relaxed font-medium text-cream">"{translatedVerse || data.verse.text}"</p>
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-amber-200/90 mt-1.5">{data.verse.reference}</p>
              </div>
            </blockquote>
          )}

          {/* Hidden ReadAloudButton stays for keyboard / a11y users and
              for the body-tap onClick to delegate into. Visually
              de-emphasised — owner asked for no colored pill. */}
          <div className="mt-4 flex flex-wrap items-center gap-2 opacity-80">
            <ReadAloudButton
              text={`${data.verse?.text ? `${translatedVerse || data.verse.text} — ${data.verse.reference || ""}. ` : ""}${translatedBody || data.text}`}
              testid="daily-devotional-read-aloud"
              nowPlayingLabel={t("today.devotionalLabel")}
              variant="light"
              prefetch
            />
          </div>
        </div>

        {/* RIGHT — devotional body. Also the primary play affordance per
            Feb 2026 owner request ("push the word and it reads"). Fills
            the wide column on desktop so the card no longer leaves dead
            space on the right. */}
        <button
          type="button"
          data-testid="daily-devotional-text"
          onClick={() => {
            const btn = document.querySelector('[data-testid="daily-devotional-read-aloud"]');
            if (btn) btn.click();
          }}
          className={`px-5 py-4 rounded-2xl border bg-black/25 border-cream/15 text-[0.95rem] md:text-base leading-relaxed whitespace-pre-wrap font-medium text-left text-cream transition-colors hover:bg-black/15 w-full h-full block ${pendingBody ? "opacity-70" : ""}`}
          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
        >
          <span className="block text-[10px] tracking-[0.22em] uppercase font-bold text-amber-200/90 mb-2.5">
            <Tr>Tap to listen</Tr>
          </span>
          {translatedBody || data.text}
          {pendingBody && (
            <span className="inline-flex items-center gap-1.5 ml-2 text-[10px] tracking-[0.2em] uppercase font-bold opacity-80 animate-pulse">
              · Translating…
            </span>
          )}
        </button>
      </div>
    </section>
  );
}
